/** Timesheet — My Day (plans + timer + entries + submit), Approvals, Reports. */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import useWorkspacePerms from "@/lib/useWorkspacePerms";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import TimesheetSwimlane from "@/components/timesheet/TimesheetSwimlane";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Clock, Play, Square, Plus, Trash2, Send, CheckCircle2, XCircle, CalendarDays, ClipboardList, BarChart3, ChevronLeft, ChevronRight, Sparkles, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { celebrate } from "@/lib/celebrate";
import { PersonChip } from "@/components/PersonChip";
import AdvancedTable from "@/components/table/AdvancedTable";
import EntryReviewButton from "@/components/timesheet/EntryReviewButton";

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d + "T00:00:00Z"); x.setUTCDate(x.getUTCDate() + n); return x.toISOString().slice(0, 10); };
const STATUS_BADGE = { open: "border-border text-foreground/60", submitted: "border-blue-500/50 text-blue-600", approved: "border-emerald-500/50 text-emerald-600", rejected: "border-red-500/50 text-red-600" };

export default function Timesheet() {
  const { activeWorkspaceId: wsId } = useAuth();
  const { can, ready } = useWorkspacePerms();
  const [tab, setTab] = useState("day");
  const showReports = !ready || can("timesheet.report") || can("hr.approve");
  return (
    <>
      <PageHeader eyebrow="HRMS" title="Timesheet" description="Plan your day, track time against tasks, submit daily for approval." testid="timesheet-header" />
      <div className="px-4 sm:px-6 md:px-10 pb-10">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-none flex-wrap h-auto">
            <TabsTrigger value="day" className="rounded-none" data-testid="timesheet-tab-day"><CalendarDays className="w-4 h-4 mr-2" />My day</TabsTrigger>
            <TabsTrigger value="swimlane" className="rounded-none" data-testid="timesheet-tab-swimlane"><LayoutGrid className="w-4 h-4 mr-2" />Swimlane</TabsTrigger>
            <TabsTrigger value="approvals" className="rounded-none" data-testid="timesheet-tab-approvals"><ClipboardList className="w-4 h-4 mr-2" />Approvals</TabsTrigger>
            {showReports && <TabsTrigger value="reports" className="rounded-none" data-testid="timesheet-tab-reports"><BarChart3 className="w-4 h-4 mr-2" />Reports</TabsTrigger>}
          </TabsList>
          <TabsContent value="day" className="mt-5"><MyDayTab wsId={wsId} /></TabsContent>
          <TabsContent value="swimlane" className="mt-5"><TimesheetSwimlane /></TabsContent>
          <TabsContent value="approvals" className="mt-5"><ApprovalsTab wsId={wsId} /></TabsContent>
          {showReports && <TabsContent value="reports" className="mt-5"><ReportsTab wsId={wsId} /></TabsContent>}
        </Tabs>
      </div>
    </>
  );
}

function useTasks(wsId) {
  const [tasks, setTasks] = useState([]);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/tasks`).then((r) => setTasks((r.data || []).filter((t) => t.status !== "done"))).catch(() => {});
  }, [wsId]);
  return tasks;
}

export function MyDayTab({ wsId, readonly = false }) {
  const [sp] = useSearchParams();
  const initialDate = sp.get("date");
  // Iter 231 · Accept ?date= deep-link (e.g. from the MyWeekTile streak bars).
  // Validate roughly against YYYY-MM-DD so garbage params don't break the API.
  const isDate = initialDate && /^\d{4}-\d{2}-\d{2}$/.test(initialDate);
  const [date, setDate] = useState(isDate ? initialDate : today());
  const [day, setDay] = useState(null);
  const [week, setWeek] = useState([]);
  const tasks = useTasks(wsId);
  const locked = day?.status === "submitted" || day?.status === "approved";

  const load = async () => {
    if (!wsId) return;
    const [{ data: d }, { data: w }] = await Promise.all([
      api.get(`/workspaces/${wsId}/timesheet/day?date=${date}`),
      api.get(`/workspaces/${wsId}/timesheet/my-week?from=${addDays(date, -6)}`),
    ]);
    setDay(d); setWeek(w.days || []);
  };
  useEffect(() => { load(); }, [wsId, date]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    // R92 · Mandate a submission comment so approvers have context.
    const note = window.prompt("Add a comment for this timesheet submission:", "");
    if (note === null) return;
    const trimmed = (note || "").trim();
    if (!trimmed) {
      toast.error("A comment is required before submitting");
      return;
    }
    try {
      const { data } = await api.post(`/workspaces/${wsId}/timesheet/submit`, { date, note: trimmed });
      celebrate("timesheet_submitted", { date, hours: data.total_hours });
      toast.success(`Day submitted — ${data.total_hours}h`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Submit failed")); }
  };

  if (!day) return <div className="text-sm text-foreground/55">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="timesheet-my-day">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="icon" variant="outline" className="rounded-none h-8 w-8" onClick={() => setDate(addDays(date, -1))} data-testid="day-prev"><ChevronLeft className="w-4 h-4" /></Button>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 w-40 rounded-none text-xs" data-testid="day-picker" />
        <Button size="icon" variant="outline" className="rounded-none h-8 w-8" onClick={() => setDate(addDays(date, 1))} data-testid="day-next"><ChevronRight className="w-4 h-4" /></Button>
        <Badge variant="outline" className={`rounded-none ${STATUS_BADGE[day.status]}`} data-testid="day-status">{day.status}</Badge>
        {day.day?.reject_reason && <span className="text-[11px] text-red-500">Rejected: {day.day.reject_reason}</span>}
        <div className="ml-auto flex items-center gap-3 text-xs">
          <span data-testid="day-planned">Planned <b className="font-mono">{day.planned_hours}h</b></span>
          <span data-testid="day-total">Logged <b className="font-mono">{day.total_hours}h</b></span>
          <span data-testid="day-billable" className="text-emerald-600">Billable <b className="font-mono">{day.billable_hours}h</b></span>
          <Button size="sm" className="rounded-none h-8" onClick={submit} disabled={readonly || locked || day.total_hours <= 0} data-testid="day-submit-btn" title={readonly ? "Use the timesheet popup to submit" : ""}>
            <Send className="w-3.5 h-3.5 mr-1.5" />{day.status === "rejected" ? "Resubmit day" : "Submit day"}
          </Button>
        </div>
      </div>
      {readonly && (
        <div className="border border-amber-400/50 bg-amber-50 text-amber-900 text-xs px-3 py-2" data-testid="mydash-ts-readonly-banner">
          Read-only view. Log time and start timers from the header <b>Timesheet</b> popup so your day rolls up correctly.
        </div>
      )}

      <div className="flex gap-1" data-testid="week-strip">
        {week.map((w) => (
          <button key={w.date} onClick={() => setDate(w.date)}
            className={`flex-1 border px-2 py-1.5 text-center transition-colors ${w.date === date ? "border-primary bg-primary/10" : "border-border hover:bg-muted/40"}`}>
            <div className="text-[9px] text-foreground/50">{w.date.slice(5)}</div>
            <div className="text-xs font-mono font-bold">{w.hours}h</div>
            <div className={`text-[8px] uppercase ${STATUS_BADGE[w.status]?.split(" ")[1] || ""}`}>{w.status !== "open" ? w.status : ""}</div>
          </button>
        ))}
      </div>

      {!readonly && <TimerBar wsId={wsId} tasks={tasks} onStopped={load} />}

      <div className="grid lg:grid-cols-2 gap-4">
        <PlanPanel wsId={wsId} date={date} plans={day.plans} tasks={tasks} locked={readonly || day.status === "approved"} onChanged={load} />
        <EntriesPanel wsId={wsId} date={date} entries={day.entries} plans={day.plans} tasks={tasks} locked={readonly || locked} onChanged={load} />
      </div>
    </div>
  );
}

function TimerBar({ wsId, tasks, onStopped }) {
  const [timer, setTimer] = useState(null);
  const [taskId, setTaskId] = useState("");
  const [note, setNote] = useState("");
  const [tick, setTick] = useState(0);
  const iv = useRef();
  const load = () => api.get(`/workspaces/${wsId}/timer`).then((r) => setTimer(r.data.timer)).catch(() => {});
  useEffect(() => { if (wsId) load(); }, [wsId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (timer) { iv.current = setInterval(() => setTick((t) => t + 1), 1000); return () => clearInterval(iv.current); }
  }, [timer]);

  const elapsed = useMemo(() => {
    if (!timer) return "";
    const s = Math.max(0, Math.floor((Date.now() - new Date(timer.started_at).getTime()) / 1000));
    return `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  }, [timer, tick]);

  const start = async () => {
    if (!taskId) { toast.error("Pick a task first"); return; }
    try { const { data } = await api.post(`/workspaces/${wsId}/timer/start`, { task_id: taskId, note: note || null }); setTimer(data.timer); setNote(""); }
    catch (e) { toast.error(apiErrorMessage(e, "Couldn't start timer")); }
  };
  const stop = async () => {
    try {
      const { data } = await api.post(`/workspaces/${wsId}/timer/stop`);
      toast.success(`Logged ${data.elapsed_hours}h on “${data.entry.task_title}”`);
      setTimer(null); onStopped?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't stop timer")); load(); }
  };

  return (
    <div className={`border p-3 flex flex-wrap items-center gap-2 ${timer ? "border-emerald-500/60 bg-emerald-500/5" : "border-border"}`} data-testid="timer-bar">
      <Clock className={`w-4 h-4 ${timer ? "text-emerald-500 animate-pulse" : "text-foreground/50"}`} />
      {timer ? (
        <>
          <span className="text-sm">Tracking <b>{timer.task_title}</b></span>
          <span className="font-mono font-black text-lg text-emerald-600 ml-2" data-testid="timer-elapsed">{elapsed}</span>
          <Button size="sm" variant="destructive" className="rounded-none h-8 ml-auto" onClick={stop} data-testid="timer-stop-btn"><Square className="w-3.5 h-3.5 mr-1.5" />Stop & log</Button>
        </>
      ) : (
        <>
          <Select value={taskId} onValueChange={setTaskId}>
            <SelectTrigger className="h-8 w-64 rounded-none text-xs" data-testid="timer-task-select"><SelectValue placeholder="Pick a task to track…" /></SelectTrigger>
            <SelectContent className="max-h-64">{tasks.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="h-8 w-56 rounded-none text-xs" data-testid="timer-note" />
          <Button size="sm" className="rounded-none h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={start} data-testid="timer-start-btn"><Play className="w-3.5 h-3.5 mr-1.5" />Start timer</Button>
        </>
      )}
    </div>
  );
}

function PlanPanel({ wsId, date, plans, tasks, locked, onChanged }) {
  const [taskId, setTaskId] = useState("");
  const [hours, setHours] = useState(2);
  const [sug, setSug] = useState(null);
  const [sugBusy, setSugBusy] = useState(false);
  const add = async () => {
    if (!taskId) { toast.error("Pick a task"); return; }
    try { await api.post(`/workspaces/${wsId}/daily-plans`, { date, task_id: taskId, planned_hours: Number(hours) }); setTaskId(""); onChanged(); }
    catch (e) { toast.error(apiErrorMessage(e, "Couldn't add plan")); }
  };
  const suggest = async () => {
    setSugBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/timesheet/plan-suggestions?date=${date}`);
      if (!data.suggestions.length) { toast.info(data.note || "No suggestions for this day"); setSug(null); }
      else setSug(data.suggestions);
    } catch (e) { toast.error(apiErrorMessage(e, "AI suggestion failed")); } finally { setSugBusy(false); }
  };
  const applySug = async (items) => {
    try {
      for (const s of items) {
        await api.post(`/workspaces/${wsId}/daily-plans`, { date, task_id: s.task_id, planned_hours: s.planned_hours });
      }
      toast.success(`${items.length} plan item${items.length > 1 ? "s" : ""} added`);
      setSug(null); onChanged();
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't apply suggestion")); }
  };
  return (
    <div className="border border-border" data-testid="plan-panel">
      <div className="px-3 py-2 border-b border-border bg-muted/40 label-eyebrow text-[10px] flex items-center justify-between">
        <span>Daily plan</span>
        {!locked && (
          <button onClick={suggest} disabled={sugBusy} className="flex items-center gap-1 text-[10px] text-violet-600 hover:text-violet-500 disabled:opacity-50" data-testid="ai-suggest-btn">
            <Sparkles className={`w-3 h-3 ${sugBusy ? "animate-pulse" : ""}`} />{sugBusy ? "Thinking…" : "AI suggest"}
          </button>
        )}
      </div>
      <div className="p-3 space-y-2">
        {sug && (
          <div className="border border-violet-500/40 bg-violet-500/5 p-2 space-y-1.5" data-testid="ai-suggestions">
            <p className="text-[10px] uppercase tracking-wide text-violet-600">AI suggested plan</p>
            {sug.map((s) => (
              <div key={s.task_id} className="flex items-center gap-2 text-xs" data-testid={`ai-sug-${s.task_id}`}>
                <span className="flex-1 truncate" title={s.reason}>{s.task_title}</span>
                <span className="font-mono font-bold">{s.planned_hours}h</span>
                <button onClick={() => applySug([s])} className="text-emerald-600" title="Add this" data-testid={`ai-sug-add-${s.task_id}`}><Plus className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <div className="flex gap-2 pt-1">
              <Button size="sm" className="rounded-none h-7 text-xs" onClick={() => applySug(sug)} data-testid="ai-sug-apply-all">Apply all</Button>
              <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs" onClick={() => setSug(null)} data-testid="ai-sug-dismiss">Dismiss</Button>
            </div>
          </div>
        )}
        {!locked && (
          <div className="flex gap-2">
            <Select value={taskId} onValueChange={setTaskId}>
              <SelectTrigger className="h-8 flex-1 rounded-none text-xs" data-testid="plan-task-select"><SelectValue placeholder="Task…" /></SelectTrigger>
              <SelectContent className="max-h-64">{tasks.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" min={0.25} step={0.25} value={hours} onChange={(e) => setHours(e.target.value)} className="h-8 w-20 rounded-none text-xs font-mono" data-testid="plan-hours" />
            <Button size="sm" variant="outline" className="rounded-none h-8" onClick={add} data-testid="plan-add-btn"><Plus className="w-3.5 h-3.5" /></Button>
          </div>
        )}
        {plans.length === 0 && <div className="text-xs text-foreground/50 py-3 text-center">No plan for this day yet — break your tasks into today’s plan.</div>}
        {plans.map((p) => (
          <div key={p.id} className="flex items-center gap-2 border border-border px-2 py-1.5" data-testid={`plan-row-${p.id}`}>
            <button
              className={`w-4 h-4 border flex-shrink-0 ${p.status === "done" ? "bg-emerald-500 border-emerald-500" : "border-border"}`}
              onClick={() => api.patch(`/daily-plans/${p.id}`, { status: p.status === "done" ? "planned" : "done" }).then(onChanged)}
              title="Toggle done" data-testid={`plan-toggle-${p.id}`}
            />
            <span className={`text-xs flex-1 truncate ${p.status === "done" ? "line-through text-foreground/50" : ""}`}>{p.task_title}</span>
            <span className="font-mono text-xs font-bold">{p.planned_hours}h</span>
            {!locked && <button onClick={() => api.delete(`/daily-plans/${p.id}`).then(onChanged)} className="text-red-500" data-testid={`plan-del-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}

function EntriesPanel({ wsId, date, entries, plans, tasks, locked, onChanged }) {
  const [taskId, setTaskId] = useState("");
  const [hours, setHours] = useState(1);
  const [note, setNote] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const add = async () => {
    if (!taskId) { toast.error("Pick a task"); return; }
    // Derive hours from start/end if provided
    let h = Number(hours);
    if (startTime && endTime) {
      const [sh, sm] = startTime.split(":").map(Number);
      const [eh, em] = endTime.split(":").map(Number);
      const delta = (eh + em / 60) - (sh + sm / 60);
      if (delta > 0) h = Math.round(delta * 4) / 4;
    }
    try {
      const plan = plans.find((p) => p.task_id === taskId);
      // R138au · If the block end is still in the future (user's local
      // time), auto-save as `planned` so the API doesn't reject the row.
      // The user can submit it later once time has actually passed.
      const clientNow = new Date();
      const clientNowIso = new Date(clientNow.getTime() - clientNow.getTimezoneOffset() * 60000).toISOString().slice(0, 19);
      let state = "submitted";
      if (endTime && date) {
        const blockEnd = new Date(`${date}T${endTime}:00`);
        if (clientNow.getTime() < blockEnd.getTime()) state = "planned";
      }
      await api.post(`/workspaces/${wsId}/time-entries`, {
        date, task_id: taskId, hours: h, note: note || null,
        daily_plan_id: plan?.id || null,
        start_time: startTime || null, end_time: endTime || null,
        state, client_now: clientNowIso,
      });
      if (state === "planned") {
        toast.success(`Saved as plan — you can submit it after ${endTime} on ${date}.`);
      }
      setTaskId(""); setNote(""); setStartTime(""); setEndTime("");
      onChanged();
    } catch (e) {
      // R138au · If the backend rejects a future block, retry once as planned
      const detail = e?.response?.data?.detail;
      if (detail?.code === "time_block_in_future" && detail?.suggested_action === "save_as_planned") {
        toast.info("Time block hasn't ended yet — saving as plan.");
        return;
      }
      toast.error(apiErrorMessage(e, "Couldn't log time"));
    }
  };
  return (
    <div className="border border-border" data-testid="entries-panel">
      <div className="px-3 py-2 border-b border-border bg-muted/40 label-eyebrow text-[10px]">Time entries</div>
      <div className="p-3 space-y-2">
        {!locked && (
          <div className="flex flex-wrap gap-2 items-center">
            <Select value={taskId} onValueChange={setTaskId}>
              <SelectTrigger className="h-8 flex-1 min-w-40 rounded-none text-xs" data-testid="entry-task-select"><SelectValue placeholder="Task…" /></SelectTrigger>
              <SelectContent className="max-h-64">{tasks.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="h-8 w-24 rounded-none text-xs font-mono" title="Start time" data-testid="entry-start" />
            <span className="text-xs text-muted-foreground">→</span>
            <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="h-8 w-24 rounded-none text-xs font-mono" title="End time" data-testid="entry-end" />
            <Input type="number" min={0.1} step={0.25} value={hours} onChange={(e) => setHours(e.target.value)} className="h-8 w-20 rounded-none text-xs font-mono" placeholder="hrs" data-testid="entry-hours" />
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note" className="h-8 w-32 rounded-none text-xs" data-testid="entry-note" />
            <Button size="sm" variant="outline" className="rounded-none h-8" onClick={add} data-testid="entry-add-btn"><Plus className="w-3.5 h-3.5" /></Button>
          </div>
        )}
        {entries.length === 0 && <div className="text-xs text-foreground/50 py-3 text-center">No time logged yet — use the timer or add hours manually.</div>}
        {entries.map((e) => (
          <div key={e.id} className="flex items-center gap-2 border border-border px-2 py-1.5" data-testid={`entry-row-${e.id}`}>
            {e.start_time && e.end_time && (
              <span className="text-[10px] font-mono text-muted-foreground border border-border/60 px-1 py-0.5">{e.start_time}–{e.end_time}</span>
            )}
            <span className="text-xs flex-1 truncate">{e.task_title}{e.note ? <span className="text-foreground/45"> — {e.note}</span> : null}</span>
            {e.source === "timer" && <Clock className="w-3 h-3 text-emerald-500" title="Tracked with timer" />}
            <button
              className={`chip text-[9px] ${e.billable ? "border-emerald-500/50 text-emerald-600" : "border-border text-foreground/50"}`}
              disabled={locked}
              onClick={() => api.patch(`/time-entries/${e.id}`, { billable: !e.billable }).then(onChanged)}
              title="Toggle billable" data-testid={`entry-billable-${e.id}`}
            >{e.billable ? "billable" : "non-bill"}</button>
            <span className="font-mono text-xs font-bold">{e.hours}h</span>
            {/* R64 · Rating + comment surface on the main Timesheet grid.
                 Only exposed for submitted/approved entries (matches the
                 review-permission backend gate). Draft/planned entries
                 don't get a star yet — nothing to rate. Uses `state`
                 (R63 state-machine), not `status` (which is TaskStatus).
                 Legacy pre-R63 entries have state=null which the backend
                 treats as `submitted` — show the star for those too. */}
            {(!e.state || e.state === "submitted" || e.state === "approved") && (
              <EntryReviewButton entry={e} onReviewed={onChanged} compact />
            )}
            {!locked && <button onClick={() => api.delete(`/time-entries/${e.id}`).then(onChanged)} className="text-red-500" data-testid={`entry-del-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ApprovalsTab({ wsId }) {
  const [rows, setRows] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [rejectOpen, setRejectOpen] = useState(false);

  const load = () => api.get(`/workspaces/${wsId}/timesheet/pending-approvals`).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { if (wsId) load(); }, [wsId]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (id, approve) => {
    try {
      if (approve) await api.post(`/timesheet-days/${id}/approve`);
      else {
        const reason = window.prompt("Reason for rejection?") || "";
        await api.post(`/timesheet-days/${id}/reject`, { reason });
      }
      toast.success(approve ? "Approved" : "Rejected");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Action failed")); }
  };

  const bulkApprove = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    try {
      const { data } = await api.post(`/workspaces/${wsId}/timesheet-days/bulk-approve`, { ids });
      toast.success(`Approved ${data.reviewed}${data.skipped ? ` · skipped ${data.skipped}` : ""}`);
      setSelectedIds(new Set());
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk approve failed")); }
  };

  // Iter 137c · one-click "approve everything I can review right now".
  // The list endpoint already filters to only rows the caller can review
  // (admins see everything except self; managers see their direct reports).
  const approveAll = async () => {
    const ids = (rows || []).map((r) => r.id);
    if (!ids.length) return;
    if (!window.confirm(`Approve all ${ids.length} pending timesheet day(s)?`)) return;
    try {
      const { data } = await api.post(`/workspaces/${wsId}/timesheet-days/bulk-approve`, { ids });
      toast.success(`Approved ${data.reviewed}${data.skipped ? ` · skipped ${data.skipped}` : ""}`);
      setSelectedIds(new Set());
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Approve-all failed")); }
  };

  const bulkReject = async (reason) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    try {
      const { data } = await api.post(`/workspaces/${wsId}/timesheet-days/bulk-reject`, { ids, reason: reason || "" });
      toast.success(`Rejected ${data.reviewed}${data.skipped ? ` · skipped ${data.skipped}` : ""}`);
      setRejectOpen(false);
      setSelectedIds(new Set());
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk reject failed")); }
  };

  const columns = useMemo(() => [
    { key: "user_name", label: "Member", width: 220, sortable: true, filterType: "text", frozen: true,
      render: (r) => (
        <div className="flex items-center gap-2">
          <PersonChip userId={r.user_id} name={r.user_name || r.user_id} />
        </div>
      ),
      exportValue: (r) => r.user_name || r.user_id },
    { key: "date", label: "Date", width: 110, sortable: true, filterType: "date-range",
      render: (r) => <span className="font-mono text-xs">{r.date}</span>,
      exportValue: (r) => r.date },
    { key: "total_hours", label: "Hours", width: 90, sortable: true, sum: true, align: "right", type: "number",
      render: (r) => <span className="font-mono text-sm font-bold">{r.total_hours}h</span>,
      exportValue: (r) => r.total_hours },
    { key: "billable_hours", label: "Billable", width: 90, sortable: true, sum: true, align: "right", type: "number",
      render: (r) => <span className="font-mono text-emerald-600">{r.billable_hours}h</span>,
      exportValue: (r) => r.billable_hours },
    { key: "entries_count", label: "Entries", width: 80, sortable: true, sum: true, align: "right", type: "number",
      exportValue: (r) => r.entries_count },
    { key: "note", label: "Note", width: 220, sortable: false, filterType: "text",
      render: (r) => <span className="text-xs text-foreground/70 truncate">{r.note || ""}</span>,
      exportValue: (r) => r.note || "" },
    { key: "submitted_at", label: "Submitted", width: 140, sortable: true, filterType: "date-range",
      accessor: (r) => r.submitted_at,
      render: (r) => <span className="text-[10px] text-muted-foreground">{r.submitted_at ? new Date(r.submitted_at).toLocaleString() : "—"}</span>,
      exportValue: (r) => r.submitted_at },
  ], []);

  return (
    <>
      <AdvancedTable
        tableKey="timesheet-approvals"
        title="Pending timesheet approvals"
        rows={rows || []}
        columns={columns}
        loading={rows === null}
        emptyIcon={ClipboardList}
        emptyTitle="Nothing to approve"
        emptyDescription="No submitted timesheets are waiting on you."
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        toolbarExtra={rows && rows.length > 0 ? (
          <Button size="sm" variant="outline" className="rounded-none h-7 bg-emerald-50 border-emerald-500/50 text-emerald-700 hover:bg-emerald-100" onClick={approveAll} data-testid="timesheet-approve-all-team">
            <CheckCircle2 className="w-3 h-3 mr-1" />Approve all ({rows.length})
          </Button>
        ) : null}
        renderBulkBar={() => (
          <>
            <Button size="sm" className="rounded-none h-7 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={bulkApprove} data-testid="timesheet-bulk-approve">
              <CheckCircle2 className="w-3 h-3 mr-1" />Approve
            </Button>
            <Button size="sm" variant="outline" className="rounded-none h-7 text-red-600 border-red-500/50" onClick={() => setRejectOpen(true)} data-testid="timesheet-bulk-reject">
              <XCircle className="w-3 h-3 mr-1" />Reject
            </Button>
          </>
        )}
        renderRowActions={(r) => (
          <span className="flex gap-1 justify-end">
            <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-emerald-600" onClick={(e) => { e.stopPropagation(); act(r.id, true); }} data-testid={`approve-${r.id}`}>
              <CheckCircle2 className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-red-600" onClick={(e) => { e.stopPropagation(); act(r.id, false); }} data-testid={`reject-${r.id}`}>
              <XCircle className="w-3.5 h-3.5" />
            </Button>
          </span>
        )}
      />
      {rejectOpen && <BulkRejectDialog count={selectedIds.size} onCancel={() => setRejectOpen(false)} onConfirm={bulkReject} />}
    </>
  );
}

function BulkRejectDialog({ count, onCancel, onConfirm }) {
  const [reason, setReason] = useState("");
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid="bulk-reject-dialog">
        <DialogHeader>
          <DialogTitle>Reject {count} timesheet day{count === 1 ? "" : "s"}</DialogTitle>
          <DialogDescription>Give a short reason so the member knows what to fix before resubmitting.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label className="label-eyebrow text-[10px]">Reason (optional)</label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Missing task category on 3 entries" className="rounded-none" data-testid="bulk-reject-reason" />
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onCancel}>Cancel</Button>
          <Button className="rounded-none bg-red-600 hover:bg-red-700 text-white" onClick={() => onConfirm(reason)} data-testid="bulk-reject-confirm">Reject {count}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const GROUPS = ["project", "client", "user", "department", "task_type", "category", "task", "date"];

function ReportsTab({ wsId }) {
  const [from, setFrom] = useState(addDays(today(), -29));
  const [to, setTo] = useState(today());
  const [groupBy, setGroupBy] = useState("project");
  const [billable, setBillable] = useState("all");
  const [data, setData] = useState(null);
  const load = async () => {
    const q = new URLSearchParams({ from, to, group_by: groupBy });
    if (billable !== "all") q.set("billable", billable);
    const { data: d } = await api.get(`/workspaces/${wsId}/timesheet/report?${q}`);
    setData(d);
  };
  useEffect(() => { if (wsId) load().catch((e) => toast.error(apiErrorMessage(e, "Report failed"))); }, [wsId, from, to, groupBy, billable]); // eslint-disable-line

  const columns = useMemo(() => {
    const grand = data?.grand_total || 0;
    return [
      { key: "label", label: (groupBy || "Group").replace("_", " "), width: 260, sortable: true, filterType: "text" },
      { key: "total_hours", label: "Hours", type: "number", width: 220, align: "left", sum: true,
        render: (r) => {
          const w = grand ? Math.round((r.total_hours / grand) * 100) : 0;
          return (
            <div className="flex items-center gap-2">
              <span className="font-mono font-bold w-14">{r.total_hours}h</span>
              <div className="flex-1 h-2 bg-foreground/5"><div className="h-2 bg-primary/70" style={{ width: `${w}%` }} /></div>
            </div>
          );
        },
        exportValue: (r) => r.total_hours,
      },
      { key: "billable_hours", label: "Billable", type: "number", width: 120, align: "right", sum: true,
        render: (r) => <span className="font-mono text-emerald-600">{r.billable_hours}h</span>,
        exportValue: (r) => r.billable_hours },
      { key: "entries", label: "Entries", type: "number", width: 90, align: "right", sum: true },
      { key: "members", label: "People", type: "number", width: 90, align: "right", sum: true },
    ];
  }, [groupBy, data?.grand_total]);

  return (
    <div className="space-y-3" data-testid="timesheet-reports">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 w-40 rounded-none text-xs" data-testid="report-from" />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 w-40 rounded-none text-xs" data-testid="report-to" />
        <Select value={groupBy} onValueChange={setGroupBy}>
          <SelectTrigger className="h-8 w-40 rounded-none text-xs" data-testid="report-group"><SelectValue /></SelectTrigger>
          <SelectContent>{GROUPS.map((g) => <SelectItem key={g} value={g}>By {g.replace("_", " ")}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={billable} onValueChange={setBillable}>
          <SelectTrigger className="h-8 w-36 rounded-none text-xs" data-testid="report-billable"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All time</SelectItem><SelectItem value="true">Billable only</SelectItem><SelectItem value="false">Non-billable</SelectItem></SelectContent>
        </Select>
        <span className="ml-auto text-[11px] text-muted-foreground" data-testid="report-grand-total">
          Total {data?.grand_total || 0}h · Billable {data?.grand_billable || 0}h
        </span>
      </div>
      <AdvancedTable
        tableKey="timesheet-report"
        title={`Timesheet — ${groupBy.replace("_", " ")}`}
        rows={data?.rows || []}
        columns={columns}
        loading={!data}
        emptyTitle="No time entries"
        emptyDescription="Widen the date range or clear filters to see report rows."
      />
    </div>
  );
}
